
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export type UserRole = 'admin' | 'marketing' | 'support' | 'integrations_manager';

interface UseUserRolesOptions {
  userId?: string | null;
}

export const useUserRoles = (options?: UseUserRolesOptions) => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const userId = options?.userId;
    
    const fetchUserRoles = async () => {
      if (!userId) {
        setRoles([]);
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .rpc('get_user_roles', { _user_id: userId });
        
        if (error) {
          throw error;
        }
        
        setRoles(data || []);
      } catch (error) {
        console.error("Erro ao buscar funções do usuário:", error);
        toast({
          title: "Erro",
          description: "Não foi possível buscar funções do usuário",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserRoles();
  }, [options?.userId, toast]);
  
  const hasRole = (role: UserRole) => {
    return roles.includes(role);
  };
  
  const addRole = async (userId: string, role: UserRole) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role });
      
      if (error) throw error;
      
      // Refresh roles if adding role to current user
      if (options?.userId && userId === options.userId) {
        setRoles(prev => [...prev, role]);
      }
      
      toast({
        title: "Sucesso",
        description: `Função ${role} adicionada com sucesso`,
      });
      
      return true;
    } catch (error: any) {
      console.error("Erro ao adicionar função:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível adicionar a função",
        variant: "destructive",
      });
      
      return false;
    }
  };
  
  const removeRole = async (userId: string, role: UserRole) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', role);
      
      if (error) throw error;
      
      // Refresh roles if removing role from current user
      if (options?.userId && userId === options.userId) {
        setRoles(prev => prev.filter(r => r !== role));
      }
      
      toast({
        title: "Sucesso",
        description: `Função ${role} removida com sucesso`,
      });
      
      return true;
    } catch (error: any) {
      console.error("Erro ao remover função:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível remover a função",
        variant: "destructive",
      });
      
      return false;
    }
  };
  
  return { roles, hasRole, addRole, removeRole, isLoading };
};
